/**
 * `youtube` — a channel's videos, then their timed transcripts (sources.md "youtube").
 *
 * fetch: the channel's Atom feed (`relay-only`, no CORS). Each video arrives with `contentState: 'pending'`
 * and `extra = { videoId }`; `complete` later asks InnerTube's player endpoint (the relay's one POST rule)
 * for caption tracks, fetches the chosen track and renders `<p data-t>` paragraphs, then `<hr>` and the
 * description.
 */

import { Parser } from 'htmlparser2';
import type { FetchResponse } from '../contracts/platform.js';
import type { MediaItem, RawItem, SourceClient, SourceFetchContext, SourceFetchResult, SourceRuntime, TranscriptCue, YoutubeConfig } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { htmlToText } from '../kernel/html.js';
import { attr, isRecord, parseFeed, textOf, toArray, type XmlNode } from './feed.js';
import { assertOk, contextConditionalHeaders, escapeHtml, invalidInput, newestFirst, parseDate, parseHttpUrl, parseJson, textToParagraphs, validatorsOf } from './http.js';
import { FEED_ACCEPT } from './rss.js';

export const YOUTUBE_ORIGIN = 'https://www.youtube.com';
/** The relay's single POST rule (packages/server control config `relay.post_rules`) — exact URL. */
export const PLAYER_URL = `${YOUTUBE_ORIGIN}/youtubei/v1/player`;

export const feedUrlOf = (channelId: string): string => `${YOUTUBE_ORIGIN}/feeds/videos.xml?channel_id=${encodeURIComponent(channelId)}`;
export const watchUrlOf = (videoId: string): string => `${YOUTUBE_ORIGIN}/watch?v=${encodeURIComponent(videoId)}`;
export const thumbnailOf = (videoId: string): string => `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;

/** The InnerTube client is the engine_config row `youtube.client` (read through SourceRuntime.config). */
type InnertubeClient = { clientName: string; clientVersion: string; androidSdkVersion: number };

/** sources.md: a new paragraph after ≈ 60 s, or at a gap > 4 s following sentence-ending punctuation. */
const PARAGRAPH_SECONDS = 60;
const PARAGRAPH_GAP_SECONDS = 4;

const CHANNEL_ID = /^UC[A-Za-z0-9_-]{22}$/;

// ── resolve ────────────────────────────────────────────────────────────────────────────────────

type Target = { channelId: string } | { pageUrl: string };

/** What the user typed → a channel id, or the YouTube page that names it. */
export function parseYoutubeAddress(address: string): Target | null {
  const raw = address.trim();
  if (CHANNEL_ID.test(raw)) return { channelId: raw };
  if (/^@[\w.-]+$/.test(raw)) return { pageUrl: `${YOUTUBE_ORIGIN}/${raw}` };
  const url = parseHttpUrl(raw);
  if (!url) return null;
  const host = url.hostname.toLowerCase();
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    return id ? { pageUrl: watchUrlOf(id) } : null;
  }
  if (host !== 'youtube.com' && !host.endsWith('.youtube.com')) return null;
  const parts = url.pathname.split('/').filter((p) => p !== '');
  const first = parts[0] ?? '';
  if (first === 'channel' && parts[1] && CHANNEL_ID.test(parts[1])) return { channelId: parts[1] };
  const fromQuery = url.searchParams.get('channel_id');
  if (fromQuery && CHANNEL_ID.test(fromQuery)) return { channelId: fromQuery };
  if (first === 'watch' && url.searchParams.get('v')) return { pageUrl: watchUrlOf(url.searchParams.get('v') as string) };
  if (first === 'shorts' && parts[1]) return { pageUrl: watchUrlOf(parts[1]) };
  if (first.startsWith('@') || first === 'c' || first === 'user') return { pageUrl: `${YOUTUBE_ORIGIN}${url.pathname}` };
  return null;
}

/** The canonical channel id named by a channel or watch page. */
export function channelIdFromPage(html: string): string | null {
  const patterns = [
    /<link rel="canonical" href="https:\/\/www\.youtube\.com\/channel\/(UC[A-Za-z0-9_-]{22})"/,
    /<meta itemprop="(?:identifier|channelId)" content="(UC[A-Za-z0-9_-]{22})"/,
    /feeds\/videos\.xml\?channel_id=(UC[A-Za-z0-9_-]{22})/,
    /"(?:externalId|channelId)":"(UC[A-Za-z0-9_-]{22})"/,
  ];
  for (const re of patterns) {
    const m = re.exec(html);
    if (m?.[1]) return m[1];
  }
  return null;
}

async function relayGet(runtime: SourceRuntime, url: string, headers: Record<string, string> = {}): Promise<FetchResponse> {
  return runtime.fetcher.fetch({ url, method: 'GET', headers, route: 'relay-only' });
}

// ── the feed ───────────────────────────────────────────────────────────────────────────────────

export function videoItems(xml: string, config: Pick<YoutubeConfig, 'skipShorts'>): { title: string; items: RawItem[] } {
  const feed = parseFeed(xml, 'The YouTube channel feed');
  const items: RawItem[] = [];
  for (const entry of feed.entries) {
    const item = videoItem(entry, config);
    if (item) items.push(item);
  }
  return { title: feed.title, items };
}

function videoItem(entry: XmlNode, config: Pick<YoutubeConfig, 'skipShorts'>): RawItem | null {
  const videoId = textOf(entry.videoId);
  if (!videoId) return null;
  const links = toArray(entry.link).map((l) => attr(l, 'href') ?? '');
  if (config.skipShorts && links.some((href) => href.includes('/shorts/'))) return null;
  const group = toArray(entry.group).find(isRecord);
  const description = group ? textOf(group.description) : '';
  const item: RawItem = {
    externalId: videoId,
    url: watchUrlOf(videoId),
    imageUrl: thumbnailOf(videoId),
    contentState: 'pending',
    extra: { videoId },
  };
  const title = htmlToText(textOf(entry.title));
  if (title) item.title = title;
  const publishedAt = parseDate(textOf(entry.published));
  if (publishedAt !== undefined) item.publishedAt = publishedAt;
  const author = isRecord(entry.author) ? textOf(entry.author.name) : '';
  if (author) item.author = author;
  // sources.md: the description is plain text; the normaliser turns `text` into paragraphs with links.
  item.text = description || title || videoId;
  return item;
}

// ── captions ───────────────────────────────────────────────────────────────────────────────────

/** Parse either caption XML dialect: `<timedtext><body><p t d>` (ms) or `<transcript><text start dur>` (s). */
export function parseCaptions(xml: string): TranscriptCue[] {
  const cues: TranscriptCue[] = [];
  let current: { startSec: number; durSec: number; text: string; dialect: 'p' | 'text' } | null = null;
  let depth = 0;
  const parser = new Parser(
    {
      onopentag(name, attribs) {
        if (current) {
          depth++;
          return;
        }
        if (name === 'p' && attribs.t !== undefined) {
          current = { startSec: Number(attribs.t) / 1000, durSec: Number(attribs.d ?? 0) / 1000, text: '', dialect: 'p' };
          depth = 0;
        } else if (name === 'text' && attribs.start !== undefined) {
          current = { startSec: Number(attribs.start), durSec: Number(attribs.dur ?? 0), text: '', dialect: 'text' };
          depth = 0;
        }
      },
      ontext(text) {
        if (current) current.text += text;
      },
      onclosetag(name) {
        if (!current) return;
        if (depth > 0) {
          depth--;
          return;
        }
        if ((current.dialect === 'p' && name === 'p') || (current.dialect === 'text' && name === 'text')) {
          // srv1 text is HTML-escaped inside XML ("&amp;#39;"): htmlToText decodes that second layer.
          const text = htmlToText(current.text).replace(/\s+/g, ' ').trim();
          if (text !== '' && Number.isFinite(current.startSec)) {
            cues.push({ startSec: current.startSec, durSec: Number.isFinite(current.durSec) ? current.durSec : 0, text });
          }
          current = null;
        }
      },
    },
    { xmlMode: true, decodeEntities: true },
  );
  parser.write(xml);
  parser.end();
  return cues;
}

/** Group cues into paragraphs (sources.md rule) and render `<p data-t="<start s>">`. */
export function transcriptHtml(cues: readonly TranscriptCue[]): string {
  const paragraphs: Array<{ start: number; texts: string[] }> = [];
  let prev: TranscriptCue | null = null;
  for (const cue of cues) {
    const para = paragraphs[paragraphs.length - 1];
    const gap = prev ? cue.startSec - (prev.startSec + prev.durSec) : 0;
    const sentenceEnded = prev !== null && /[.!?…]["')\]]?$/.test(prev.text);
    if (!para || cue.startSec - para.start >= PARAGRAPH_SECONDS || (gap > PARAGRAPH_GAP_SECONDS && sentenceEnded)) {
      paragraphs.push({ start: cue.startSec, texts: [cue.text] });
    } else {
      para.texts.push(cue.text);
    }
    prev = cue;
  }
  return paragraphs.map((p) => `<p data-t="${Math.floor(p.start)}">${escapeHtml(p.texts.join(' '))}</p>`).join('');
}

interface CaptionTrack {
  baseUrl: string;
  languageCode: string;
  kind: string | null;
}

function captionTracks(player: unknown): CaptionTrack[] {
  if (!isRecord(player) || !isRecord(player.captions)) return [];
  const renderer = player.captions.playerCaptionsTracklistRenderer;
  if (!isRecord(renderer)) return [];
  return toArray(renderer.captionTracks)
    .filter(isRecord)
    .map((t) => ({
      baseUrl: typeof t.baseUrl === 'string' ? t.baseUrl : '',
      languageCode: typeof t.languageCode === 'string' ? t.languageCode : '',
      kind: typeof t.kind === 'string' ? t.kind : null,
    }))
    .filter((t) => t.baseUrl.startsWith('https://'));
}

/** First language of `languages` (manual before auto-generated), else the first track. */
export function pickTrack(tracks: readonly CaptionTrack[], languages: readonly string[]): CaptionTrack | null {
  for (const lang of languages) {
    const l = lang.toLowerCase();
    const matching = tracks.filter((t) => t.languageCode.toLowerCase() === l || t.languageCode.toLowerCase().startsWith(`${l}-`));
    const manual = matching.find((t) => t.kind !== 'asr');
    if (manual) return manual;
    if (matching[0]) return matching[0];
  }
  return tracks[0] ?? null;
}

function playerBody(videoId: string, client: InnertubeClient): string {
  return JSON.stringify({
    context: { client: { ...client, hl: 'en' } },
    videoId,
    contentCheckOk: true,
    racyCheckOk: true,
  });
}

/** A transient failure: the pipeline retries per content.retry_minutes. */
function isTransient(error: unknown): boolean {
  return error instanceof DigesterError && (error.code === 'fetch_failed' || error.code === 'rate_limited' || error.code === 'fetch_blocked');
}

async function completeVideo(ctx: SourceFetchContext<'youtube'>, item: { externalId: string; extra: Record<string, unknown> | null }) {
  const videoId = typeof item.extra?.videoId === 'string' ? item.extra.videoId : item.externalId;
  const runtime = ctx.runtime;
  let player: unknown;
  try {
    const response = await runtime.fetcher.fetch({
      url: PLAYER_URL,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: playerBody(videoId, runtime.config.get('youtube.client')),
      route: 'relay-only',
    });
    if (response.status < 200 || response.status >= 300) return null;
    player = parseJson(response.text, 'The YouTube player endpoint');
  } catch (error) {
    if (isTransient(error)) return null;
    throw error;
  }
  const status = isRecord(player) && isRecord(player.playabilityStatus) ? player.playabilityStatus.status : null;
  if (status === 'LOGIN_REQUIRED' || status === null) return null;
  const details = isRecord(player) && isRecord(player.videoDetails) ? player.videoDetails : {};
  const description = typeof details.shortDescription === 'string' ? details.shortDescription : '';
  const lengthSeconds = Number(details.lengthSeconds);
  const durationSec = Number.isFinite(lengthSeconds) && lengthSeconds > 0 ? lengthSeconds : undefined;
  const url = watchUrlOf(videoId);
  const media: MediaItem[] = [{ type: 'video', url, thumbnailUrl: thumbnailOf(videoId), ...(durationSec !== undefined ? { durationSec } : {}) }];
  const base = { ...(item.extra ?? {}), videoId, ...(durationSec !== undefined ? { durationSec } : {}) };
  const descriptionHtml = textToParagraphs(description);

  const track = pickTrack(captionTracks(player), ctx.source.config.languages);
  if (status !== 'OK' || !track) {
    return { html: descriptionHtml || '<p></p>', media, extra: { ...base, transcript: 'none' } };
  }
  let cues: TranscriptCue[];
  try {
    const response = await relayGet(runtime, track.baseUrl, { Accept: 'text/xml, application/xml, */*' });
    if (response.status < 200 || response.status >= 300) return null;
    cues = parseCaptions(response.text);
  } catch (error) {
    if (isTransient(error)) return null;
    throw error;
  }
  // A 200 with an empty body is the token-gated caption URL: not available yet from this address.
  if (cues.length === 0) return null;
  const html = `${transcriptHtml(cues)}${descriptionHtml ? `<hr>${descriptionHtml}` : ''}`;
  return {
    html,
    media,
    extra: { ...base, transcriptLang: track.languageCode, transcript: track.kind === 'asr' ? 'auto' : 'manual' },
  };
}

// ── the client ─────────────────────────────────────────────────────────────────────────────────

export const youtubeClient: SourceClient<'youtube'> = {
  kind: 'youtube',
  defaultPullMinutes: 60,

  async resolve(input, runtime) {
    const target = input.config?.channelId && CHANNEL_ID.test(input.config.channelId) ? { channelId: input.config.channelId } : parseYoutubeAddress(input.address);
    if (!target) throw invalidInput('Enter a YouTube channel URL, an @handle, a UC… channel id or a video URL.');
    const now = runtime.clock.now();
    let channelId: string;
    if ('channelId' in target) {
      channelId = target.channelId;
    } else {
      const page = await relayGet(runtime, target.pageUrl, { Accept: 'text/html' });
      assertOk(page, { credential: false, what: 'YouTube', now });
      const found = channelIdFromPage(page.text);
      if (!found) throw new DigesterError('parse_failed', 'Could not find the channel id on that YouTube page.');
      channelId = found;
    }
    const feed = await relayGet(runtime, feedUrlOf(channelId), { Accept: FEED_ACCEPT });
    assertOk(feed, { credential: false, what: 'The YouTube channel feed', now });
    const { title } = videoItems(feed.text, { skipShorts: false });
    return {
      kind: 'youtube',
      locator: `youtube:channel:${channelId}`,
      name: title || channelId,
      config: {
        kind: 'youtube',
        channelId,
        languages: Array.isArray(input.config?.languages) ? input.config.languages.filter((l): l is string => typeof l === 'string') : [],
        skipShorts: input.config?.skipShorts ?? true,
      },
      requiredSecrets: [],
    };
  },

  async fetch(ctx): Promise<SourceFetchResult> {
    const { channelId } = ctx.source.config;
    const response = await relayGet(ctx.runtime, feedUrlOf(channelId), { Accept: FEED_ACCEPT, ...contextConditionalHeaders(ctx) });
    assertOk(response, { credential: false, what: ctx.source.name || 'The YouTube channel feed', now: ctx.runtime.clock.now() });
    const cursor = ctx.cursor ?? {};
    if (response.status === 304) return { items: [], cursor, notModified: true };
    const items = newestFirst(videoItems(response.text, ctx.source.config).items, ctx.maxItems);
    const newest = items[0];
    return { items, cursor: newest ? { ...cursor, newestVideoId: newest.externalId, newestAt: newest.publishedAt ?? null } : cursor, ...validatorsOf(response) };
  },

  complete: completeVideo,
};

